import {type AiSearchAskSource, resetAiSearchSession} from 'analytics/aiSearchAnalytics';
import {showSearchAtom} from 'atoms/search';
import type {AiQueryTurn} from 'components/ai/aiQueryConversation';
import type {ChatMention} from 'components/DSYS/chat/chatMentions';
import type {ProfileAskKind} from 'components/profile/profileAsk';
import {atom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';

export type AiDrawerView = 'closed' | 'expanded' | 'minimized';

export const aiDrawerViewAtom = atom<AiDrawerView>('closed');

// Persisted AI-search preferences share the `aiSearch:` localStorage namespace
// (matches the `dashboard:` convention used elsewhere). Keep new keys here.

/** Which loading-glyph animation the chat thinking indicator uses (design toggle). */
export type AiSearchLoadingGlyphVariant = 'converge' | 'pulse';
export const aiSearchLoadingGlyphVariantAtom = atomWithStorage<AiSearchLoadingGlyphVariant>(
  'aiSearch:loadingGlyphVariant',
  'pulse'
);
/**
 * Desktop drawer width in px. The drawer is resizable by dragging its left edge;
 * the chosen width persists across sessions. Clamped at render time to the
 * viewport so a stored value can never push the drawer off screen. The default
 * fits the whole reading column (745) plus the thread's side padding.
 */
export const AI_DRAWER_DEFAULT_WIDTH_PX = 800;
export const AI_DRAWER_MIN_WIDTH_PX = 400;
export const aiSearchDrawerWidthAtom = atomWithStorage<number>(
  'aiSearch:drawerWidth',
  AI_DRAWER_DEFAULT_WIDTH_PX
);

export const aiSearchTurnsAtom = atom<AiQueryTurn[]>([]);
export const aiSearchConversationIdAtom = atom<string | null>(null);
export const aiSearchIsStreamingAtom = atom(false);
export const aiSearchComposerDraftAtom = atom('');
/** A single message queued while the previous turn is still streaming. */
export const aiSearchQueuedMessageAtom = atom<string | null>(null);
/** Set by search entry points; consumed once by the drawer to start a turn. */
export const aiSearchPendingQuestionAtom = atom<string | null>(null);
/** Ask analytics source for the pending question; consumed with the question. */
export const aiSearchPendingSourceAtom = atom<AiSearchAskSource | null>(null);
/** The Knowledge page a chip's pending question is about; consumed with the question. */
export const aiSearchPendingKnowledgePageIdAtom = atom<string | null>(null);
/** People the pending question is about (e.g. the profile a chip is on); consumed with it. */
export const aiSearchPendingMentionsAtom = atom<ChatMention[] | null>(null);
/** The profile chip the pending question came from; consumed with the question. */
export const aiSearchPendingProfileAskAtom = atom<ProfileAskKind | null>(null);

export const openAiSearchAndAskAtom = atom(
  null,
  (
    _get,
    set,
    question: string,
    source?: AiSearchAskSource,
    opts?: {knowledgePageId?: string; mentions?: ChatMention[]; profileAsk?: ProfileAskKind}
  ) => {
    const trimmed = question.trim();
    if (!trimmed) {
      return;
    }
    resetAiSearchSession();
    set(aiSearchPendingQuestionAtom, trimmed);
    set(aiSearchPendingSourceAtom, source ?? null);
    set(aiSearchPendingKnowledgePageIdAtom, opts?.knowledgePageId ?? null);
    set(aiSearchPendingMentionsAtom, opts?.mentions?.length ? opts.mentions : null);
    set(aiSearchPendingProfileAskAtom, opts?.profileAsk ?? null);
    set(aiDrawerViewAtom, 'expanded');
    set(showSearchAtom, false);
  }
);
